import React from 'react';
import Icon from '../../../components/AppIcon';

const PhilosophySection = () => {
  const philosophyPillars = [
    {
      id: 1,
      icon: "Target",
      title: "Strategic Focus",
      description: "We don't just invest capital; we architect digital futures through calculated boldness and operational expertise.",
      details: "Our investment thesis centers on early-stage companies with disruptive potential in e-commerce, SaaS, and emerging technologies. We provide strategic guidance that transforms innovative ideas into scalable market leaders."
    },
    {
      id: 2,
      icon: "Users",
      title: "Partnership Approach",
      description: "Sophisticated yet approachable, we build lasting relationships with entrepreneurs who think beyond conventional boundaries.",
      details: "Every partnership begins with understanding your vision. We combine traditional investment wisdom with digital-first innovation, ensuring founders receive both capital and the strategic support needed for sustainable growth."
    },
    {
      id: 3,
      icon: "TrendingUp",
      title: "Data-Driven Decisions",
      description: "Human-centered approach backed by comprehensive market analysis and performance metrics.",
      details: "Our investment decisions are grounded in rigorous data analysis, market research, and trend forecasting. We leverage advanced analytics to identify opportunities and mitigate risks while maintaining a distinctly European perspective on global markets."
    },
    {
      id: 4,
      icon: "Globe",
      title: "European Excellence",
      description: "Hamburg-based expertise with global ambition, bridging traditional European values with international innovation.",
      details: "Rooted in Hamburg's rich trading heritage, we understand both local market dynamics and global opportunities. Our European perspective brings precision, reliability, and long-term thinking to every investment decision."
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-brand bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Icon name="Lightbulb" size={20} className="text-white" />
            </div>
            <span className="text-primary font-semibold text-lg">Investment Philosophy</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Beyond Capital:
            <span className="text-gradient-brand block">Building Digital Futures</span>
          </h2>
          
          <p className="text-xl text-muted-foreground leading-relaxed">
            REXFEL embodies the intersection of traditional investment wisdom and digital-first innovation. 
            We represent calculated boldness—the confidence to back disruptive ideas while providing the 
            operational expertise to scale them successfully.
          </p>
        </div>

        {/* Philosophy Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {philosophyPillars?.map((pillar, index) => (
            <div 
              key={pillar?.id}
              className={`group bg-card rounded-brand-lg p-8 shadow-brand hover-lift transition-brand border border-border scroll-reveal stagger-${index + 1}`}
            >
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-14 h-14 rounded-brand bg-gradient-to-br from-primary to-accent flex items-center justify-center group-hover:scale-110 transition-brand">
                  <Icon name={pillar?.icon} size={24} className="text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-foreground mb-2">{pillar?.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{pillar?.description}</p>
                </div>
              </div>
              
              <div className="pl-18">
                <p className="text-sm text-muted-foreground leading-relaxed">{pillar?.details}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Mission Statement */}
        <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-brand-lg p-8 lg:p-12 border border-primary/10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-16 h-16 rounded-brand bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-6">
              <Icon name="Compass" size={28} className="text-white" />
            </div>
            
            <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-6">Our Mission</h3>
            
            <blockquote className="text-lg lg:text-xl text-muted-foreground leading-relaxed italic mb-8">
              "To be the ideal partner for entrepreneurs who think beyond conventional boundaries, 
              providing not just capital but the strategic expertise and operational support needed 
              to transform innovative ideas into market-leading digital solutions."
            </blockquote>
            
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center space-x-2 bg-white/50 rounded-brand px-4 py-2">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span className="text-sm font-medium text-foreground">Strategic Investment</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/50 rounded-brand px-4 py-2">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span className="text-sm font-medium text-foreground">Operational Expertise</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/50 rounded-brand px-4 py-2">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span className="text-sm font-medium text-foreground">Digital Innovation</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/50 rounded-brand px-4 py-2">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span className="text-sm font-medium text-foreground">Long-term Partnership</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PhilosophySection;